import * as React from 'react';
import { Button, Input, Row, Col, Space, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { DiscountValueType } from 'app/shared/model';
import { EditableDiscountBand } from 'app/pages/DiscountEditor/modules/DiscountActions';
import {
    mergeDiscreteDuplicateTierValidation,
    discreteBandsDifferFromMerge,
} from 'app/pages/DiscountEditor/components/EditDiscountActionDialog/discountTierDuplicateValidation';
import {
    DISCOUNT_REQUIRED_FIELD_MESSAGE,
    validateDiscreteMinUnits,
    validateMonetaryDiscountValue,
    validatePercentDiscountValue,
    DISCOUNT_MONETARY_INPUT_REGEX,
    DISCOUNT_PERCENT_INPUT_REGEX,
    normalizeMonetaryDiscountOnBlur,
    normalizePercentDiscountOnBlur,
    isMonetaryDiscountValueType,
    clampMonetaryDiscountInput,
    clampPercentDiscountInput,
    shouldShowMonetaryDiscountValueMessageInline,
} from 'app/pages/DiscountEditor/components/EditDiscountActionDialog/discountBandFieldValidation';

const { Text } = Typography;

interface DiscreteBandEditorProps {
    bands: Readonly<EditableDiscountBand>[];
    onUpdateBands: (bands: Readonly<EditableDiscountBand>[]) => void;
    valueType: DiscountValueType;
    showValidation: boolean;
    readOnly: boolean;
    currencyCode: string;
    currencySymbol: string;
}

function discreteFromMessage(from: string): string {
    if (from === '') return DISCOUNT_REQUIRED_FIELD_MESSAGE;
    return validateDiscreteMinUnits(from) ?? '';
}

function discreteValueMessage(value: string, valueType: DiscountValueType): string {
    if (value === '') return DISCOUNT_REQUIRED_FIELD_MESSAGE;
    if (valueType === DiscountValueType.Percent) {
        return validatePercentDiscountValue(value) ?? '';
    }
    if (isMonetaryDiscountValueType(valueType)) {
        return validateMonetaryDiscountValue(value) ?? '';
    }
    return '';
}

function commitBands(nextBands: EditableDiscountBand[], props: DiscreteBandEditorProps) {
    if (
        props.valueType === DiscountValueType.AmountPerUnit ||
        props.valueType === DiscountValueType.OverrideUnitPrice
    ) {
        props.onUpdateBands(
            mergeDiscreteDuplicateTierValidation(nextBands, props.valueType, props.currencyCode)
        );
    } else {
        props.onUpdateBands(nextBands);
    }
}

function errorLabel(message: string | undefined, showValidation: boolean) {
    if (!message || !showValidation) return null;

    return <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>{message}</Text>;
}

function addTier(props: DiscreteBandEditorProps) {
    const bands = props.bands.slice();
    bands.push({
        from: '',
        fromMessage: DISCOUNT_REQUIRED_FIELD_MESSAGE,
        to: '',
        toMessage: DISCOUNT_REQUIRED_FIELD_MESSAGE,
        value: '',
        valueMessage: DISCOUNT_REQUIRED_FIELD_MESSAGE,
        isValid: false,
    });

    commitBands(bands, props);
}

function changeMinUnits(validateAndNormalize: boolean, index: number, newValue: string, props: DiscreteBandEditorProps) {
    const band = props.bands[index];

    if (!/^[0-9]*$/.test(newValue)) return;
    if (validateAndNormalize && newValue !== '') newValue = parseInt(newValue, 10).toString();
    if (newValue === band.from) return;

    const fromMsg = discreteFromMessage(newValue);
    const valueMsg = discreteValueMessage(band.value, props.valueType);
    const newBand: EditableDiscountBand = {
        ...band,
        from: newValue,
        fromMessage: fromMsg || undefined,
        valueMessage: valueMsg || undefined,
        isValid: !fromMsg && !valueMsg,
    };

    const newBands = props.bands.slice();
    newBands[index] = newBand;

    commitBands(newBands, props);
}

function changeValue(validateAndNormalize: boolean, index: number, newValue: string, props: DiscreteBandEditorProps) {
    const band = props.bands[index];

    switch (props.valueType) {
        case DiscountValueType.AmountPerUnit:
        case DiscountValueType.OverrideUnitPrice:
            newValue = clampMonetaryDiscountInput(newValue);
            if (newValue !== '' && !DISCOUNT_MONETARY_INPUT_REGEX.test(newValue)) return;
            if (validateAndNormalize && newValue !== '') newValue = normalizeMonetaryDiscountOnBlur(newValue);
            break;
        default:
            newValue = clampPercentDiscountInput(newValue);
            if (newValue !== '' && !DISCOUNT_PERCENT_INPUT_REGEX.test(newValue)) return;
            if (validateAndNormalize && newValue !== '') newValue = normalizePercentDiscountOnBlur(newValue);
            break;
    }
    if (newValue === band.value) return;

    const fromMsg = discreteFromMessage(band.from);
    const valueMsg = discreteValueMessage(newValue, props.valueType);
    const newBand: EditableDiscountBand = {
        ...band,
        value: newValue,
        fromMessage: fromMsg || undefined,
        valueMessage: valueMsg || undefined,
        isValid: !fromMsg && !valueMsg,
    };

    const newBands = props.bands.slice();
    newBands[index] = newBand;

    commitBands(newBands, props);
}

function removeDiscountTier(index: number, props: DiscreteBandEditorProps) {
    const newBands = props.bands.slice();
    newBands.splice(index, 1);
    commitBands(newBands, props);
}

export function DiscreteBandEditor(props: DiscreteBandEditorProps) {
    React.useEffect(() => {
        if (
            props.valueType !== DiscountValueType.AmountPerUnit &&
            props.valueType !== DiscountValueType.OverrideUnitPrice
        ) {
            return;
        }
        const merged = mergeDiscreteDuplicateTierValidation(
            props.bands,
            props.valueType,
            props.currencyCode
        );
        if (!discreteBandsDifferFromMerge(props.bands, merged)) {
            return;
        }
        props.onUpdateBands(merged);
    }, [props.currencyCode, props.valueType]);

    return (
        <div>
            {props.bands.map((band, index) => {
                const showValueMessage = shouldShowMonetaryDiscountValueMessageInline(
                    props.showValidation,
                    props.valueType,
                    band.valueMessage
                );
                return (
                <Row key={index} gutter={16} align="top" style={{ marginBottom: 16 }}>
                    <Col span={props.bands.length > 1 ? 10 : 12}>
                        <div style={{ marginBottom: 4 }}>
                            <Text strong style={{ fontSize: 14, color: '#262626' }}>
                                Min. units
                            </Text>
                        </div>
                        <Input
                            status={props.showValidation && band.fromMessage ? 'error' : ''}
                            value={band.from}
                            onChange={(e) => changeMinUnits(false, index, e.currentTarget.value, props)}
                            onBlur={(e) => changeMinUnits(true, index, e.currentTarget.value, props)}
                            disabled={props.readOnly}
                        />
                        {errorLabel(band.fromMessage, props.showValidation)}
                    </Col>
                    <Col span={props.bands.length > 1 ? 10 : 12}>
                        <div style={{ marginBottom: 4 }}>
                            <Text strong style={{ fontSize: 14, color: '#262626' }}>
                                {props.valueType === DiscountValueType.Percent
                                    ? 'Percentage discount'
                                    : props.valueType === DiscountValueType.OverrideUnitPrice
                                      ? 'Override the unit price'
                                      : 'Amount off per unit'}
                            </Text>
                        </div>
                    {props.valueType === DiscountValueType.Percent ? (
                        <Space.Compact style={{ width: '100%' }}>
                            <Input
                                status={showValueMessage && band.valueMessage ? 'error' : ''}
                                value={band.value}
                                onChange={(e) => changeValue(false, index, e.currentTarget.value, props)}
                                onBlur={(e) => changeValue(true, index, e.currentTarget.value, props)}
                                disabled={props.readOnly}
                                style={{ width: '100%' }}
                            />
                            <Space.Addon>%</Space.Addon>
                        </Space.Compact>
                    ) : (
                        <Input
                            status={showValueMessage && band.valueMessage ? 'error' : ''}
                            value={band.value}
                            onChange={(e) => changeValue(false, index, e.currentTarget.value, props)}
                            onBlur={(e) => changeValue(true, index, e.currentTarget.value, props)}
                            disabled={props.readOnly}
                            prefix={
                                props.valueType === DiscountValueType.AmountPerUnit ||
                                props.valueType === DiscountValueType.OverrideUnitPrice
                                    ? props.currencySymbol
                                    : undefined
                            }
                        />
                    )}
                        {errorLabel(band.valueMessage, showValueMessage)}
                    </Col>
                    {props.bands.length > 1 && (
                        <Col span={4} style={{ paddingTop: 28 }}>
                            <Button
                                icon={<DeleteOutlined />}
                                disabled={props.readOnly}
                                onClick={() => removeDiscountTier(index, props)}
                                danger
                            />
                        </Col>
                    )}
                </Row>
                );
            })}
            <Button type="default" disabled={props.readOnly} onClick={() => addTier(props)} style={{ marginTop: 8 }}>
                Add tier
            </Button>
        </div>
    );
}